import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faPlus, faChevronRight } from "@fortawesome/free-solid-svg-icons";

const DesktopNavigation = ({
  categories,
  loadingCategories,
  isAuthenticated,
  location
}) => {
  const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [activeSubcategory, setActiveSubcategory] = useState(null);
  const moreDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const subcategoryDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreDropdownRef.current && !moreDropdownRef.current.contains(event.target)) {
        setIsMoreDropdownOpen(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setHoveredCategory(null);
        setActiveSubcategory(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Maximum number of categories to show before adding "More" dropdown
  const MAX_VISIBLE_CATEGORIES = 11;

  const getLinkClass = (path) => {
    return location.pathname === path
      ? "flex items-center space-x-2 bg-blue-500 text-white px-3 py-2 rounded"
      : "flex items-center space-x-2 hover:bg-gray-100 px-3 py-2 rounded-lg transition";
  };

  // Determine which categories to show and which to put in "More" dropdown
  const visibleCategories = categories.slice(0, MAX_VISIBLE_CATEGORIES);
  const moreCategories = categories.length > MAX_VISIBLE_CATEGORIES 
    ? categories.slice(MAX_VISIBLE_CATEGORIES)
    : [];

  const toggleMoreDropdown = () => {
    setIsMoreDropdownOpen(!isMoreDropdownOpen);
  };

  // Recursive function to render nested subcategories
  const renderSubcategories = (subcategories, level = 0) => {
    if (!subcategories || subcategories.length === 0) return null;

    return (
      <div 
        className={`absolute left-full top-0 bg-white shadow-lg rounded-md py-2 z-50 min-w-[200px] ${level > 0 ? 'mt-0' : 'mt-2'}`}
        style={{ marginLeft: level > 0 ? '1px' : '0' }}
        ref={level === 0 ? subcategoryDropdownRef : null}
      >
        {subcategories.map((subcategory) => (
          <div 
            key={subcategory.id} 
            className="relative group"
            onMouseEnter={() => setActiveSubcategory(subcategory.id)}
            onMouseLeave={() => setActiveSubcategory(null)}
          >
            <Link
              to={subcategory.path || `/category/${subcategory.id}`}
              className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => {
                setHoveredCategory(null);
                setActiveSubcategory(null);
              }}
            >
              <span>{subcategory.name}</span>
              {subcategory.subcategories && subcategory.subcategories.length > 0 && (
                <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
              )}
            </Link>
            
            {/* Render nested subcategories */}
            {subcategory.subcategories && 
             subcategory.subcategories.length > 0 && 
             activeSubcategory === subcategory.id && (
              renderSubcategories(subcategory.subcategories, level + 1)
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="hidden lg:block bg-gray-100 border-t">
      <div className="container mx-auto flex justify-between items-center p-3 text-sm">
        <div className="flex flex-wrap gap-2 md:gap-6 relative">
          {loadingCategories ? (
            <div className="flex space-x-6">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-8 w-20 bg-gray-200 rounded animate-pulse"></div>
              ))}
            </div>
          ) : (
            <>
              {visibleCategories.map((category) => (
                <div 
                  key={category.id} 
                  className="relative"
                  ref={categoryDropdownRef}
                  onMouseEnter={() => setHoveredCategory(category.id)}
                  onMouseLeave={() => {
                    setHoveredCategory(null);
                    setActiveSubcategory(null);
                  }}
                >
                  <Link 
                    to={category.path}
                    className={getLinkClass(category.path)}
                  >
                    <span className="ml-2">{category.name}</span>
                    {category.subcategories && category.subcategories.length > 0 && (
                      <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
                    )}
                  </Link>
                  
                  {/* Category dropdown with subcategories */}
                  {hoveredCategory === category.id && 
                   category.subcategories && 
                   category.subcategories.length > 0 && (
                    <div className="absolute left-0 top-full mt-2 w-64 bg-white rounded-md shadow-lg py-2 z-50">
                      <div className="px-4 py-2 font-semibold border-b">
                        {category.name}
                      </div>
                      <div className="max-h-96 overflow-y-auto">
                        {category.subcategories.map((subcategory) => (
                          <div 
                            key={subcategory.id} 
                            className="relative group"
                            onMouseEnter={() => setActiveSubcategory(subcategory.id)}
                            onMouseLeave={() => setActiveSubcategory(null)}
                          >
                            <Link
                              to={subcategory.path || `/category/${subcategory.id}`}
                              className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              onClick={() => {
                                setHoveredCategory(null);
                                setActiveSubcategory(null);
                              }}
                            >
                              <span>{subcategory.name}</span>
                              {subcategory.subcategories && subcategory.subcategories.length > 0 && (
                                <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                              )}
                            </Link>
                            
                            {/* Render nested subcategories */}
                            {subcategory.subcategories && 
                             subcategory.subcategories.length > 0 && 
                             activeSubcategory === subcategory.id && (
                              renderSubcategories(subcategory.subcategories)
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {moreCategories.length > 0 && (
                <div className="relative" ref={moreDropdownRef}>
                  <button 
                    onClick={toggleMoreDropdown}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-200 ${
                      isMoreDropdownOpen ? "bg-gray-200" : ""
                    }`}
                  >
                    <span>More</span>
                    <FontAwesomeIcon 
                      icon={faChevronDown} 
                      className={`text-xs transition-transform ${isMoreDropdownOpen ? 'transform rotate-180' : ''}`}
                    />
                  </button>
                  
                  {isMoreDropdownOpen && (
                    <div className="absolute left-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                      {moreCategories.map((category) => (
                        <Link
                          key={category.id}
                          to={category.path}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsMoreDropdownOpen(false)}
                        >
                          {category.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        
        {isAuthenticated && (
          <Link to="/become-seller" className="flex items-center space-x-2 bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg">
            <FontAwesomeIcon icon={faPlus} />
            <span className="ml-2">Become a Seller</span>
          </Link>
        )}
      </div>
    </div>
  );
};

export default DesktopNavigation;

// import React, { useState, useRef, useEffect } from "react";
// import { Link } from "react-router-dom";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faChevronDown, faPlus, faChevronRight } from "@fortawesome/free-solid-svg-icons";

// const DesktopNavigation = ({
//   categories,
//   loadingCategories,
//   isAuthenticated,
//   location
// }) => {
//   const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
//   const [hoveredCategory, setHoveredCategory] = useState(null);
//   const [activeSubcategory, setActiveSubcategory] = useState(null);
//   const moreDropdownRef = useRef(null);
//   const categoryDropdownRef = useRef(null);
//   const subcategoryDropdownRef = useRef(null);

//   // Close dropdowns when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (moreDropdownRef.current && !moreDropdownRef.current.contains(event.target)) {
//         setIsMoreDropdownOpen(false);
//       }
//       if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
//         setHoveredCategory(null);
//         setActiveSubcategory(null);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   // Maximum number of categories to show before adding "More" dropdown
//   const MAX_VISIBLE_CATEGORIES = 11;

//   const getLinkClass = (path) => {
//     return location.pathname === path
//       ? "flex items-center space-x-2 bg-blue-500 text-white px-3 py-2 rounded"
//       : "flex items-center space-x-2 hover:bg-gray-100 px-3 py-2 rounded-lg transition";
//   };

//   // Determine which categories to show and which to put in "More" dropdown
//   const visibleCategories = categories.slice(0, MAX_VISIBLE_CATEGORIES);
//   const moreCategories = categories.length > MAX_VISIBLE_CATEGORIES 
//     ? categories.slice(MAX_VISIBLE_CATEGORIES)
//     : [];

//   const toggleMoreDropdown = () => {
//     setIsMoreDropdownOpen(!isMoreDropdownOpen);
//   };

//   // Recursive function to render nested subcategories
//   const renderSubcategories = (subcategories, level = 0) => {
//     if (!subcategories || subcategories.length === 0) return null;

//     return (
//       <div 
//         className={`absolute left-full top-0 bg-white shadow-lg rounded-md py-2 z-50 min-w-[200px] ${level > 0 ? 'mt-0' : 'mt-2'}`}
//         style={{ marginLeft: level > 0 ? '1px' : '0' }}
//         ref={level === 0 ? subcategoryDropdownRef : null}
//       >
//         {subcategories.map((subcategory) => (
//           <div 
//             key={subcategory.id} 
//             className="relative group"
//             onMouseEnter={() => setActiveSubcategory(subcategory.id)}
//             onMouseLeave={() => setActiveSubcategory(null)}
//           >
//             <Link
//               to={subcategory.path || `/category/${subcategory.id}`}
//               className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
//               onClick={() => {
//                 setHoveredCategory(null);
//                 setActiveSubcategory(null);
//               }}
//             >
//               <span>{subcategory.name}</span>
//               {subcategory.subcategories && subcategory.subcategories.length > 0 && (
//                 <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
//               )}
//             </Link>
            
//             {/* Render nested subcategories */}
//             {subcategory.subcategories && 
//              subcategory.subcategories.length > 0 && 
//              activeSubcategory === subcategory.id && (
//               renderSubcategories(subcategory.subcategories, level + 1)
//             )}
//           </div>
//         ))}
//       </div>
//     );
//   };

//   return (
//     <div className="bg-gray-100 border-t">
//       <div className="container mx-auto flex justify-between items-center p-3 text-sm">
//         <div className="flex flex-wrap gap-2 md:gap-6 relative">
//           {loadingCategories ? (
//             <div className="flex space-x-6">
//               {[...Array(5)].map((_, i) => (
//                 <div key={i} className="h-8 w-20 bg-gray-200 rounded animate-pulse"></div>
//               ))}
//             </div>
//           ) : (
//             <>
//               {visibleCategories.map((category) => (
//                 <div 
//                   key={category.id} 
//                   className="relative"
//                   ref={categoryDropdownRef}
//                   onMouseEnter={() => setHoveredCategory(category.id)}
//                   onMouseLeave={() => {
//                     setHoveredCategory(null);
//                     setActiveSubcategory(null);
//                   }}
//                 >
//                   <Link 
//                     to={category.path}
//                     className={getLinkClass(category.path)}
//                   >
//                     <span className="ml-2">{category.name}</span>
//                     {category.subcategories && category.subcategories.length > 0 && (
//                       <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
//                     )}
//                   </Link>
                  
//                   {/* Category dropdown with subcategories */}
//                   {hoveredCategory === category.id && 
//                    category.subcategories && 
//                    category.subcategories.length > 0 && (
//                     <div className="absolute left-0 top-full mt-2 w-64 bg-white rounded-md shadow-lg py-2 z-50">
//                       <div className="px-4 py-2 font-semibold border-b">
//                         {category.name}
//                       </div>
//                       <div className="max-h-96 overflow-y-auto">
//                         {category.subcategories.map((subcategory) => (
//                           <div 
//                             key={subcategory.id} 
//                             className="relative group"
//                             onMouseEnter={() => setActiveSubcategory(subcategory.id)}
//                             onMouseLeave={() => setActiveSubcategory(null)}
//                           >
//                             <Link
//                               to={subcategory.path || `/category/${subcategory.id}`}
//                               className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
//                               onClick={() => {
//                                 setHoveredCategory(null);
//                                 setActiveSubcategory(null);
//                               }}
//                             >
//                               <span>{subcategory.name}</span>
//                               {subcategory.subcategories && subcategory.subcategories.length > 0 && (
//                                 <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
//                               )}
//                             </Link>
                            
//                             {/* Render nested subcategories */}
//                             {subcategory.subcategories && 
//                              subcategory.subcategories.length > 0 && 
//                              activeSubcategory === subcategory.id && (
//                               renderSubcategories(subcategory.subcategories)
//                             )}
//                           </div>
//                         ))}
//                       </div>
//                     </div>
//                   )}
//                 </div>
//               ))}
              
//               {moreCategories.length > 0 && (
//                 <div className="relative" ref={moreDropdownRef}>
//                   <button 
//                     onClick={toggleMoreDropdown}
//                     className={`flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-200 ${
//                       isMoreDropdownOpen ? "bg-gray-200" : ""
//                     }`}
//                   >
//                     <span>More</span>
//                     <FontAwesomeIcon 
//                       icon={faChevronDown} 
//                       className={`text-xs transition-transform ${isMoreDropdownOpen ? 'transform rotate-180' : ''}`}
//                     />
//                   </button>
                  
//                   {isMoreDropdownOpen && (
//                     <div className="absolute left-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
//                       {moreCategories.map((category) => (
//                         <Link
//                           key={category.id}
//                           to={category.path}
//                           className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
//                           onClick={() => setIsMoreDropdownOpen(false)}
//                         >
//                           {category.name}
//                         </Link>
//                       ))}
//                     </div>
//                   )}
//                 </div>
//               )}
//             </>
//           )}
//         </div>
        
//         {isAuthenticated && (
//           <Link to="/become-seller" className="flex items-center space-x-2 bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg">
//             <FontAwesomeIcon icon={faPlus} />
//             <span className="ml-2">Become a Seller</span>
//           </Link>
//         )}
//       </div>
//     </div>
//   );
// };

// export default DesktopNavigation;
// import React, { useRef, useState } from "react";
// import { Link } from "react-router-dom";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faChevronDown, faPlus } from "@fortawesome/free-solid-svg-icons";

// const DesktopNavigation = ({
//   categories,
//   loadingCategories,
//   isAuthenticated,
//   location
// }) => {
//   const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
//   const moreDropdownRef = useRef(null);

//   // Maximum number of categories to show before adding "More" dropdown
//   const MAX_VISIBLE_CATEGORIES = 11;

//   const getLinkClass = (path) => {
//     return location.pathname === path
//       ? "flex items-center space-x-2 bg-blue-500 text-white px-3 py-2 rounded"
//       : "flex items-center space-x-2 hover:bg-gray-100 px-3 py-2 rounded-lg transition";
//   };

//   // Determine which categories to show and which to put in "More" dropdown
//   const visibleCategories = categories.slice(0, MAX_VISIBLE_CATEGORIES);
//   const moreCategories = categories.length > MAX_VISIBLE_CATEGORIES 
//     ? categories.slice(MAX_VISIBLE_CATEGORIES)
//     : [];

//   const toggleMoreDropdown = () => {
//     setIsMoreDropdownOpen(!isMoreDropdownOpen);
//   };

//   return (
//     <div className="bg-gray-100 border-t">
//       <div className="container mx-auto flex justify-between items-center p-3 text-sm">
//         <div className="flex flex-wrap gap-2 md:gap-6">
//           {loadingCategories ? (
//             <div className="flex space-x-6">
//               {[...Array(5)].map((_, i) => (
//                 <div key={i} className="h-8 w-20 bg-gray-200 rounded animate-pulse"></div>
//               ))}
//             </div>
//           ) : (
//             <>
//               {visibleCategories.map((category) => (
//                 <Link 
//                   key={category.id} 
//                   to={category.path}
//                   className={getLinkClass(category.path)}
//                 >
//                   <span className="ml-2">{category.name}</span>
//                 </Link>
//               ))}
              
//               {moreCategories.length > 0 && (
//                 <div className="relative" ref={moreDropdownRef}>
//                   <button 
//                     onClick={toggleMoreDropdown}
//                     className={`flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-200 ${
//                       isMoreDropdownOpen ? "bg-gray-200" : ""
//                     }`}
//                   >
//                     <span>More</span>
//                     <FontAwesomeIcon 
//                       icon={faChevronDown} 
//                       className={`text-xs transition-transform ${isMoreDropdownOpen ? 'transform rotate-180' : ''}`}
//                     />
//                   </button>
                  
//                   {isMoreDropdownOpen && (
//                     <div className="absolute left-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
//                       {moreCategories.map((category) => (
//                         <Link
//                           key={category.id}
//                           to={category.path}
//                           className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
//                           onClick={() => setIsMoreDropdownOpen(false)}
//                         >
//                           {category.name}
//                         </Link>
//                       ))}
//                     </div>
//                   )}
//                 </div>
//               )}
//             </>
//           )}
//         </div>
        
//         {isAuthenticated && (
//           <Link to="/become-seller" className="flex items-center space-x-2 bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg">
//             <FontAwesomeIcon icon={faPlus} />
//             <span className="ml-2">Become a Seller</span>
//           </Link>
//         )}
//       </div>
//     </div>
//   );
// };

// export default DesktopNavigation;